import { Col, Row, Space, Spin, Typography } from 'antd';
import { HeartPulse, Percent, Thermometer } from 'lucide-react';
import { useEffect, useState } from 'react'
import { getRisk } from '../../utils/exhibitions/FlippingCardsExhibition';
import * as libCon from '../../community-hats-js-library/Constants.js';
import * as locCon from '../../LocalConstants.js';
import ProgressiveLineChart from "../../elements/exhibitions/FlippingCardsExhibition.js";
import { RefParticipantData, RefSateliteData, RefSimulationSteps } from "../../hooks/exhibitions/FlippingCardsExhibition.js";
import { isNullOrUndefined, sleep } from "../../community-hats-js-library/utils/generalFunctions.js";
import { format, parse } from 'date-fns';
import { DATE_STRING } from '../../community-hats-js-library/utils/dateFunctions.js';

const { Title } = Typography;

export const DateTitle = ({ simulationSteps, currentIndex }) => {

    const [dateString, setDateString] = useState("")

    useEffect(() => {

        if (!isNullOrUndefined(simulationSteps)) {
            let date = parse(simulationSteps[currentIndex], DATE_STRING, new Date())
            let d = format(date, 'MMM d')
            let h = format(date, 'h:mma').toLowerCase()

            setDateString(`${d}, ${h}`)
        }


    }, [simulationSteps, currentIndex])


    return (<Title style={{ margin: "0px", marginTop: "5px", padding: "0px" }}>{dateString}</Title>
    )

}


export default function OutdoorTemperatureProgressiveChart({ width = "90vw", height = "15vh", currentIndex }) {

    const [status, data] = RefSateliteData()


    return (
        status === libCon.STATUS_OK
            ? <ProgressiveLineChart data={data}
                width={width}
                height={height}
                currentIndex={currentIndex}
                xValue={libCon.DATETIME}
                yValue={libCon.TEMPERATURE}
                strokeColor={"var(--exb-therm-color)"} />
            : <div></div>
    )
}



const ICON_TRANSPARENCY = 0.23



const StatItem = ({ icon: Icon, value, unit, color }) => {
    return (
        <div style={{ position: 'relative', width: '40px', height: '40px', fontSize: '100%' }}>

            <Icon
                style={{
                    color,
                    opacity: ICON_TRANSPARENCY,
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    fontSize: '100%', // makes the icon fill the container
                    width: '100%',    // optional, doesn’t hurt
                    height: '100%',   // optional
                }}
            />
            <span
                style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    fontWeight: 'bold',
                }}
            >
                {value}{unit}
            </span>
        </div>
    );
};


const FlippingCard = ({ totalItems, data, simulationSteps, currentIndex }) => {

    const [flipped, setFlipped] = useState(false);

    // Pick a random back image once
    const [backImage] = useState(() => {
        const randomNum = Math.floor(Math.random() * 3) + 1;
        return `/img/exhibit/sister_${randomNum}.jpg`;
    });


    useEffect(() => {
        const intervalId = setInterval(() => {

            if (flipped === true)
                setFlipped(false)
            else if (Math.random() < 1 / totalItems)
                setFlipped(true)

            // Flips
        }, ((10 + 5 * (Math.random() - 0.5)) * 1000));

        // Cleanup on unmount
        return () => clearInterval(intervalId);
    }, [flipped, totalItems]);


    const [heartRate, setHeartRate] = useState(() => Math.round(data[Object.keys(data)[0]][libCon.HEART_RATE]))
    const [temperature, setTemperature] = useState(() => Math.round(data[Object.keys(data)[0]][libCon.TEMPERATURE]))
    const [humidity, setHumidity] = useState(() => Math.round(data[Object.keys(data)[0]][libCon.HUMIDITY]))
    const [risk, setRisk] = useState(0)



    useEffect(() => {

        if (isNullOrUndefined(simulationSteps) || isNullOrUndefined(data)) {
            return
        }

        let temp = async () => {

            let i = currentIndex % simulationSteps.length

            if (simulationSteps[i] in data) {
                const element = data[simulationSteps[i]]

                const hr = element[libCon.HEART_RATE]
                const temp = element[libCon.TEMPERATURE]
                const hum = element[libCon.HUMIDITY]
                const r = getRisk(hr, hum, temp);

                await sleep(locCon.EXHIBITION_FC_FREQUENCY * 0.2 * (Math.random()) * 1000)

                setHeartRate(Math.round(hr));
                setTemperature(Math.round(temp));
                setHumidity(Math.round(hum));
                setRisk(r);

            }
        }

        temp()


    }, [simulationSteps, data, currentIndex])



    return (
        <div className="flip-card" style={{
            width: `100%`, height: `100%`,
            backgroundColor: `var(--exb-risk-color-${risk + 1}-t)`,
            color: 'var(--exb-card-text)',
            borderColor: `var(--exb-risk-color-${risk + 1})`,
        }} onClick={() => setFlipped(!flipped)}>
            <div
                className="flip-card-inner"
                style={{ transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)' }}
            >
                <div
                    className="flip-card-front"
                    style={{

                    }}
                >
                    <Space direction="vertical" style={{ height: '100%', width: '100%', display: 'flex', justifyContent: 'center' }} align="center" >
                        <div style={{
                            display: 'flex',
                            width: '100%',
                            height: '100%',
                            alignItems: 'stretch',    // important so children fill parent's height
                            justifyContent: 'center',
                            // spacing between stat items (like Space size="medium")
                        }}
                        >
                            <StatItem icon={HeartPulse} value={heartRate} unit="" color="var(--exb-heart-color)" />
                            <StatItem icon={Thermometer} value={temperature} unit="°" color="var(--exb-therm-color)" />
                            <StatItem icon={Percent} value={humidity} unit="%" color="var(--exb--hum-color)" />
                        </div>
                    </Space>
                </div>
                {/* BACK */}
                <div
                    className="flip-card-back"
                    style={{ backgroundImage: `url(${backImage})` }}
                >
                </div>
            </div>
        </div >
    );
};



export const FlippingCardComponent = ({ rows = 5, cols = 8, width = 100, height = 90, currentIndex }) => {
    const gap = 6; // px gap between cards

    const cardWidth = `calc((${width}vw - ${gap}px) / ${cols})`;
    const cardHeight = `calc(${height}vh / ${rows} - ${gap}px)`;

    const [dataStatus, data, sewaIds] = RefParticipantData()

    const simulationSteps = RefSimulationSteps()

    return (
        <div
            style={{
                width: `${width}vw`,
                height: `${height}vh`,
                backgroundColor: "var(--exb-background-color)",
            }}
        >
            {
                dataStatus !== libCon.STATUS_OK
                    ? <Spin />
                    : <Row gutter={[gap, gap]}>
                        {Array.from({ length: rows }).map((_, i) =>
                            Array.from({ length: cols }).map((_, j) => (
                                <Col
                                    key={`${i}-${j}`}
                                    style={{ width: cardWidth, height: cardHeight }}
                                >
                                    <div style={{ width: "100%", height: "100%" }}>
                                        <FlippingCard totalItems={cols * rows} data={data[sewaIds[i * cols + j]]} simulationSteps={simulationSteps} currentIndex={currentIndex} />
                                    </div>
                                </Col>
                            ))
                        )}
                    </Row>
            }

        </div>
    );

}

